<i18n
  src="@/locales/views/agro_exporter/finances/harvested_batch_outcomes/new_invoice.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_process')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
    }"
  />

  <bread-crumbs :labels="[module_name, agro_exporter_process?.name]" />

  <template v-if="load_general_data">
    <alert-danger class="mt-6" :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-6">
                <label
                  :class="['label-form', isInvalidLabel('harvested_batch_id')]"
                >
                  {{ $t("model.org_invoice.harvested_batch") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="harvested_batches"
                    v-model="org_invoice.harvested_batch_id"
                    label="label"
                    :is_invalid="isInvalidSelect('harvested_batch')"
                  ></select-form>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-4">
              <div class="sm:col-span-3">
                <div class="mt-1">
                  <switch-form
                    v-model="use_invoice_issuer"
                    :label="$t('labels.use_issuer_invoice')"
                  />
                </div>
              </div>
              <div v-if="use_invoice_issuer" class="sm:col-span-6">
                <label
                  :class="['label-form', isInvalidLabel('invoice_issuer_id')]"
                >
                  {{ $t("model.org_invoice.invoice_issuer") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="invoice_issuers"
                    v-model="org_invoice.invoice_issuer_id"
                    :is_invalid="isInvalidSelect('invoice_issuer_id')"
                  ></select-form>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-4">
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('invoice_code')]">
                  {{ $t("model.org_invoice.invoice_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="org_invoice.invoice_code"
                    :class="['input-text', isInvalid('invoice_code')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('invoice_date')]">
                  {{ $t("model.org_invoice.invoice_date") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="org_invoice.invoice_date" />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('amount')]">
                  {{ $t("model.org_invoice.amount") }}
                </label>
                <div class="mt-1 flex w-full">
                  <select-form
                    class="w-1/3"
                    :options="currencies"
                    v-model="org_invoice.currency_id"
                    :disabled="currencies.length == 1"
                    :is_invalid="isInvalidSelect('currency')"
                  ></select-form>
                  <div class="flex flex-1 relative">
                    <div
                      class="flex items-center absolute inset-y-0 left-0 ml-3 text-sm leading-5"
                    >
                      {{ currency_symbol }}
                    </div>
                    <input
                      type="number"
                      v-model.number="org_invoice.amount"
                      :disabled="org_invoice_items.length > 0"
                      :class="['input-text pl-6', isInvalid('amount')]"
                    />
                  </div>
                </div>
              </div>

              <div class="sm:col-span-3">
                <label
                  :class="['label-form', isInvalidLabel('detraction_percent')]"
                >
                  {{ $t("model.org_invoice.detraction_percent") }}
                </label>
                <div class="mt-1">
                  <input
                    type="number"
                    v-model.number="org_invoice.detraction_percent"
                    :class="['input-text', isInvalid('detraction_percent')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('exchange_rate')]">
                  {{ $t("model.org_invoice.exchange_rate") }}
                </label>
                <div class="mt-1">
                  <input
                    type="number"
                    v-model.number="org_invoice.exchange_rate"
                    :class="['input-text', isInvalid('exchange_rate')]"
                  />
                </div>
              </div>
            </div>

            <div
              class="py-4"
              v-for="(org_invoice_item, index) in org_invoice_items"
              :key="index"
            >
              <!-- <div class="flex justify-end">
                <span @click.prevent="removeOrgInvoiceItem(index)">
                  <i class="fa-solid fa-xmark fa-xl text-gray-400"></i>
                </span>
              </div> -->
              <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                <div class="sm:col-span-2">
                  <label
                    :class="['label-form', isInvalidLabel(index + '_quantity')]"
                  >
                    {{ $t("model.org_invoice_item.quantity") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="org_invoice_item.quantity"
                      :class="['input-text', isInvalid(index + '_quantity')]"
                    />
                  </div>
                </div>
                <div class="sm:col-span-4">
                  <label
                    :class="[
                      'label-form',
                      isInvalidLabel(index + '_description'),
                    ]"
                  >
                    {{ $t("model.org_invoice_item.description") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="org_invoice_item.description"
                      :class="['input-text', isInvalid(index + '_description')]"
                    />
                  </div>
                </div>
                <div class="sm:col-span-2">
                  <label
                    :class="[
                      'label-form',
                      isInvalidLabel(index + '_unit_price'),
                    ]"
                  >
                    {{ $t("model.org_invoice_item.unit_price") }}
                  </label>
                  <div class="mt-1">
                    <input
                      type="text"
                      v-model.trim="org_invoice_item.unit_price"
                      :class="['input-text', isInvalid(index + '_unit_price')]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- <div class="flex justify-end mt-4">
            <white-button
              @click.prevent="addOrgInvoiceItem()"
              :label="$t('labels.add_item_button')"
            />
          </div> -->
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowProcess()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="createOrgInvoice()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import moment from "moment";
import Requester from "@/views/agro_exporter/requesters/finances/harvested_batch_outcomes/manage_org_invoice.js";

export default {
  name: "AeFinancesHarvestedBatchOutcomesNewInvoice",
  props: ["agro_exporter_process_id", "agro_exporter_id", "org_invoice_id"],
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      agro_exporter_process: {},
      container: {},
      currencies: [],
      org_invoice: {
        harvested_batch_id: null,
        invoice_date: null,
        currency_id: null,
        invoice_issuer_id: null,
        invoice_code: null,
        detraction_percent: 1.5,
        exchange_rate: null,
        issuer_type: null,
        amount: null
      },
      org_invoice_item_default: {
        quantity: null,
        description: null,
        unit_price: null,
      },
      org_invoice_items: [],
      use_invoice_issuer: false,
      harvested_batches: [],
      invoice_issuers: [],
    };
  },
  computed: {
    currency_symbol: function () {
      return this.currencies.find(
        (currency) => currency.id == this.org_invoice.currency_id
      )?.symbol;
    },
  },
  methods: {
    fetchData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchHarvestedBatches();
      await this.fetchCurrencies();
      this.addOrgInvoiceItem();
      this.load_general_data = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchHarvestedBatches: async function () {
      let response = await Requester.fetchHarvestedBatches(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.harvested_batches = response.data;
      this.harvested_batches.forEach((batch) => {
        batch["label"] = `${batch.batch_code} - ${batch.producer.fullname}`;
      });
    },
    fetchCurrencies: async function () {
      let response = await Requester.fetchCurrencies(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.currencies = response.data;
      if (this.currencies.length == 1)
        this.org_invoice.currency_id = this.currencies[0].id;
    },
    fetchInvoiceIssuers: async function () {
      if (!this.use_invoice_issuer) return;
      const batch = this.harvested_batches.find(
        (batch) => batch.id === this.org_invoice.harvested_batch_id
      );
      if (batch) {
        let response = await Requester.fetchInvoiceIssuers(
          this.agro_exporter_id,
          batch.producer_id
        );
        this.invoice_issuers = response.data;
        if (this.invoice_issuers.length == 1)
          this.org_invoice.invoice_issuer_id = this.invoice_issuers[0].id;
      }
    },
    createOrgInvoice: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.create(this.agro_exporter_id, {
          ...this.org_invoice,
          org_invoice_items: this.org_invoice_items,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowProcess();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
    addOrgInvoiceItem: function () {
      this.org_invoice_items.push({ ...this.org_invoice_item_default });
    },
    removeOrgInvoiceItem: function (index) {
      this.org_invoice_items.splice(index, 1);
      if (this.org_invoice_items.length == 0) this.addOrgInvoiceItem();
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_process"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
        },
      });
    },
    goToShowInvoice: function (org_invoice_id) {
      this.$router.push({
        name: this.routeName("show_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_id: org_invoice_id,
        },
      });
    },
  },
  watch: {
    org_invoice_items: {
      deep: true,
      handler: function (new_items) {
        let total_amount = 0;
        new_items.forEach((item) => {
          if (item.quantity && item.unit_price)
            total_amount += Number(item.quantity) * Number(item.unit_price);
        });
        if (total_amount) this.org_invoice.amount = total_amount;
        else this.org_invoice.amount = null;
      },
    },
    use_invoice_issuer: function (new_value) {
      if (new_value) {
        this.org_invoice.invoice_issuer_id = null;
        this.org_invoice.issuer_type = 2;
        this.fetchInvoiceIssuers();
      } else {
        const batch = this.harvested_batches.find(
          (batch) => batch.id === new_value
        );
        if (batch) {
          this.org_invoice.invoice_issuer_id =
            batch.producer_id ?? batch.organization_id;
          if (batch.producer_id) {
            this.org_invoice.issuer_type = 0;
          } else if (batch.organization_id) {
            this.org_invoice.issuer_type = 1;
          }
        }
      }
    },
    "org_invoice.harvested_batch_id": function (new_value) {
      const batch = this.harvested_batches.find(
        (batch) => batch.id === new_value
      );
      if (batch) {
        this.org_invoice.invoice_issuer_id =
          batch.producer_id ?? batch.organization_id;
        this.org_invoice_items[0].quantity = batch.num_packages;

        if (batch.producer_id) {
          this.org_invoice.issuer_type = 0;
        } else if (batch.organization_id) {
          this.org_invoice.issuer_type = 1;
        } else {
          this.org_invoice.issuer_type = null;
        }
      }
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id,
        },
      }).then(() => {
        this.org_invoice.invoice_date = moment().format("YYYY-MM-DD");
        this.fetchData();
      })
    );
  },
};
</script>
